/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-audio/bulbaudiotoolbar
 */

import {
  Plugin, WidgetToolbarRepository, isWidget, type ViewElement,
} from 'ckeditor5';
import { isObject } from 'lodash';
import { getToolBarConfig } from '../../editorcustomconfig.constant';

/**
 * The audio toolbar plugin. It creates a toolbar for audios that shows up when the figure is selected.
 *
 * Instances of toolbar components (e.g. buttons) are created based on the
 * {@link module:bulb-audio/BulbAudioToolbar#toolbar `bulbAudio.toolbar` configuration option}.
 *
 * @extends Plugin
 */
export default class BulbAudioToolbar extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [WidgetToolbarRepository] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbAudioToolbar' as const;
  }

  /**
   * @inheritDoc
   */
  public afterInit(): void {
    const { editor } = this;
    const { t } = editor;
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository);

    widgetToolbarRepository.register('audio', {
      ariaLabel: t('Audio toolbar'),
      items: this.normalizeDeclarativeConfig(getToolBarConfig('audio', CKEBulbData.readOnly)),
      getRelatedElement: BulbAudioToolbar.getSelectedMediaViewWidget,
    });
  }

  /**
   * Convert the dropdown definitions to their keys registered in the ComponentFactory.
   * The registration precess should be handled by the plugin which handles the UI of a particular feature.
   */
  private normalizeDeclarativeConfig(config) {
    return config.map((item: { name: any; }) => (isObject(item) ? item.name : item));
  }

  /**
   * Method that delegates whether to show the bulb audio toolbar or not.
   *
   * @param selection
   * @return {*}
   */
  private static getSelectedMediaViewWidget(selection): ViewElement {
    const viewElement = selection.getSelectedElement();

    if (viewElement && !!viewElement.getCustomProperty('audio') && isWidget(viewElement)) {
      return viewElement;
    }

    return null;
  }
}
